import { useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { ToastProvider } from '@/components/ui/Toast'
import { SessionProvider, useAuth } from '@/auth/SessionProvider'
import { AuthLayout } from '@/features/auth/AuthLayout'
import { processQueue, startUploadQueue } from '@/attachments/queue'
import { queryClient } from './queryClient'
import { router } from './router'

function Gate() {
  const { auth, refresh, signOut } = useAuth()

  // Whenever who-is-signed-in changes, re-run the route guards so the person is
  // moved to wherever they now belong (login, first-login, their home...).
  //
  // ONLY once the answer is known. While we are still 'loading' the router is not
  // on screen yet and holds a placeholder "nobody signed in" context; invalidating
  // then runs the guards against that stale placeholder and bounces the address to
  // /login, so a reload of any page (even with a valid session) lost the page you
  // were on and dropped you at the home screen.
  useEffect(() => {
    if (auth.status === 'signed-in' || auth.status === 'signed-out') void router.invalidate()
  }, [auth])

  // Receipt photos waiting on this device resume as soon as someone is signed in.
  useEffect(() => {
    if (auth.status !== 'signed-in') return
    startUploadQueue()
    void processQueue({ force: true })
  }, [auth.status])

  if (auth.status === 'loading') {
    return (
      <div className="min-h-dvh grid place-items-center" role="status" aria-label="Loading">
        <Spinner size="lg" />
      </div>
    )
  }

  if (auth.status === 'error') {
    return (
      <AuthLayout title="We could not load your account">
        <p role="alert" className="text-body text-neutral">
          {auth.message}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Button size="lg" onClick={() => void refresh()}>
            Try again
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return <RouterProvider router={router} context={{ auth }} />
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <Gate />
        </SessionProvider>
      </ToastProvider>
    </QueryClientProvider>
  )
}
